import { memo, useState } from "react";
import { Button } from "@mui/material";
import { Add } from "@mui/icons-material";
import { ActivitySectionFrame } from "../../provider/components/ActivitySectionFrame";
import { useTeleconsultoriaAgendas } from "../hooks/useTeleconsultoriaAgendas";
import type { TeleconsultoriaAgendaSummary } from "../types";
import { CreateAgendaDialog, type AgendaProfessionalOption } from "./CreateAgendaDialog";
import { EditAgendaDialog } from "./EditAgendaDialog";
import { TeleconsultoriaScheduleCard } from "./TeleconsultoriaScheduleCard";
import { TeleconsultoriaScheduleDrawer } from "./TeleconsultoriaScheduleDrawer";

/**
 * Agendas de teleconsultoria (componente novo, base para evoluir o ScheduleManage):
 * card com o essencial + Ver horários e consultas (Drawer) e Editar.
 * Só lista agendas ativas (o filtro fica no hook), por isso não há mais Desativar.
 * `practitionerReference`: só as do profissional; sem ele, todas da clínica.
 * `createPresetProfessional`: profissional já escolhido ao criar (no diálogo).
 */
export const TeleconsultoriaAgendasSection = memo(function TeleconsultoriaAgendasSection({
  title,
  practitionerReference,
  showPractitioner = false,
  createPresetProfessional,
}: {
  title: string;
  practitionerReference?: string;
  showPractitioner?: boolean;
  createPresetProfessional?: AgendaProfessionalOption;
}) {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const { agendas, freeSlotsById, isLoading, hasError, isSaving, reload, update } =
    useTeleconsultoriaAgendas(practitionerReference);
  const [openAgenda, setOpenAgenda] = useState<TeleconsultoriaAgendaSummary | null>(null);
  const [editingAgenda, setEditingAgenda] = useState<TeleconsultoriaAgendaSummary | null>(null);

  const createButton = (
    <Button
      variant="outlined"
      startIcon={<Add />}
      onClick={() => setIsCreateOpen(true)}
      sx={{ textTransform: "none", alignSelf: "flex-start" }}
    >
      Criar agenda de teleconsultoria
    </Button>
  );

  return (
    <>
      <ActivitySectionFrame
        title={title}
        subtitle="Horários que as clínicas afiliadas podem marcar."
        toolbar={agendas.length ? createButton : undefined}
        isLoading={isLoading}
        hasError={hasError}
        errorText="Não foi possível carregar as agendas."
        isEmpty={!agendas.length}
        emptyText="Nenhuma agenda de teleconsultoria ativa. Crie uma para oferecer horários às clínicas afiliadas."
        onRefresh={reload}
      >
        {agendas.map((agenda) => (
          <TeleconsultoriaScheduleCard
            key={agenda.id}
            agenda={agenda}
            freeSlots={freeSlotsById[agenda.id]}
            showPractitioner={showPractitioner}
            onOpen={setOpenAgenda}
            onEdit={setEditingAgenda}
          />
        ))}
      </ActivitySectionFrame>
      {/* Estado vazio: a ação principal fica logo abaixo da explicação. */}
      {!isLoading && !hasError && !agendas.length && createButton}

      <CreateAgendaDialog
        open={isCreateOpen}
        presetProfessional={createPresetProfessional}
        onClose={() => setIsCreateOpen(false)}
        onCreated={reload}
      />
      <TeleconsultoriaScheduleDrawer agenda={openAgenda} onClose={() => setOpenAgenda(null)} />
      <EditAgendaDialog
        agenda={editingAgenda}
        isSaving={isSaving}
        onClose={() => setEditingAgenda(null)}
        onSave={update}
      />
    </>
  );
});
